import { useState } from 'react'
import { Download, Loader2, Trash2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'
import { WavePlayer } from '@/components/ui/WavePlayer'
import { API_BASE_URL } from '@/lib/config'
import { useAuthStore } from '@/stores/useAuthStore'
import {
  useCallRecordings,
  useDeleteCallRecording,
} from '@/api/callLogs'
import { useAccess } from '@/hooks/useAccess'

interface Props {
  companyId: number
  callId: number
  onClose: () => void
  /**
   * Audio d'un appel pris par un agent IA (chemin relatif sans jeton).
   * Sert de repli quand l'appel n'a pas de CallRecording : les appels IA
   * antérieurs à la migration Twilio du 11 août 2026 n'en ont pas, leur
   * audio vit dans AICallLog et se streame par le proxy dédié.
   */
  aiAudioUrl?: string | null
}

/**
 * Player inline avec waveform pour un enregistrement d'appel.
 * Charge le 1er recording disponible, gère play/pause + download + delete.
 */
export function InlineRecordingPlayer({ companyId, callId, onClose, aiAudioUrl }: Props) {
  const { t } = useTranslation()
  const { data: recordings, isLoading } = useCallRecordings(companyId, callId)
  const deleteRecording = useDeleteCallRecording(companyId, callId)
  const [confirmDelete, setConfirmDelete] = useState(false)
  // Delete and download: owner and managers only (the server refuses the rest).
  const { canManageRecordings } = useAccess()

  const recording = recordings?.[0]

  // Le proxy IA authentifie par ?token= (un lecteur audio ne peut pas
  // envoyer d'en-tête), comme le font déjà les URLs d'enregistrements.
  const token = useAuthStore.getState().accessToken
  const aiUrl =
    aiAudioUrl && token
      ? `${API_BASE_URL}${aiAudioUrl}${aiAudioUrl.includes('?') ? '&' : '?'}token=${token}`
      : null

  // L'enregistrement (chaîne Twilio) prime : pour un appel donné les deux
  // sources ne coexistent pas, et s'il n'y a ni l'un ni l'autre on garde
  // le message « introuvable » existant.
  const audioUrl = recording?.audio_url || aiUrl
  const isProcessing = recording?.status === 'processing'

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-3">
        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
      </div>
    )
  }

  if (!recording && !aiUrl) {
    return (
      <div className="flex items-center justify-between gap-3 py-2">
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {t('manager.history.player.notFound')}
        </p>
        <CloseButton onClose={onClose} />
      </div>
    )
  }

  if (isProcessing || !audioUrl) {
    return (
      <div className="flex items-center justify-between gap-3 py-2">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {t('manager.history.player.processing')}
          </p>
        </div>
        <CloseButton onClose={onClose} />
      </div>
    )
  }

  const handleDownload = () => {
    const sep = audioUrl.includes('?') ? '&' : '?'
    const a = document.createElement('a')
    a.href = `${audioUrl}${sep}download=true`
    a.download = `recording_${callId}.mp3`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  const handleConfirmDelete = async () => {
    if (!recording) return
    try {
      await deleteRecording.mutateAsync(recording.id)
      toast.success(t('manager.history.player.deleted'))
      setConfirmDelete(false)
      onClose()
    } catch {
      toast.error(t('manager.history.player.deleteError'))
    }
  }

  return (
    <>
      <WavePlayer audioUrl={audioUrl} />

      <div className="flex items-center gap-1 ml-3 flex-shrink-0">
        {/* Télécharger et Supprimer n'existent que pour un CallRecording :
            l'audio IA legacy n'a pas de ressource à supprimer, et son proxy
            ne force pas la pièce jointe au téléchargement. */}
        {recording && canManageRecordings && (
          <>
            <ActionButton
              title={t('manager.history.player.download')}
              tone="emerald"
              onClick={handleDownload}
            >
              <Download className="w-3.5 h-3.5" />
            </ActionButton>
            <ActionButton
              title={t('manager.history.player.delete')}
              tone="red"
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </ActionButton>
          </>
        )}
        <ActionButton
          title={t('manager.history.player.close')}
          tone="slate"
          onClick={onClose}
        >
          <X className="w-3.5 h-3.5" />
        </ActionButton>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('manager.history.player.deleteTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('manager.history.player.deleteDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteRecording.isPending}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                void handleConfirmDelete()
              }}
              disabled={deleteRecording.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleteRecording.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {t('manager.history.player.confirmDelete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function ActionButton({
  title,
  tone,
  onClick,
  children,
}: {
  title: string
  tone: 'emerald' | 'red' | 'slate'
  onClick: () => void
  children: React.ReactNode
}) {
  const toneClass = {
    emerald:
      'hover:bg-emerald-100 dark:hover:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400',
    red: 'hover:bg-red-100 dark:hover:bg-red-500/20 text-red-600 dark:text-red-400',
    slate:
      'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 dark:text-slate-500',
  }[tone]

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={cn(
        'p-1.5 rounded-lg transition-colors flex-shrink-0',
        toneClass,
      )}
    >
      {children}
    </button>
  )
}

function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <button
      type="button"
      onClick={onClose}
      className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 transition-colors flex-shrink-0"
    >
      <X className="w-3.5 h-3.5" />
    </button>
  )
}
